/**
 * The identity check across an upload (TOK-32, anonymization specification section 11): what the
 * files state about identity, compared before anything is sent, so a site needs to describe nothing
 * about how it numbers its patients. A study, series or image under two patient IDs, and one patient
 * ID whose files differ both in birth date and in name, hold the files concerned; patient IDs that
 * differ only in leading zeros, and files of one patient ID that differ in one respect, are warnings.
 * The browser's copy of `identity_check` in packages/pedfx/adapter; testdata/identity/check.json
 * holds both to the same cases.
 */
import type { IdentityEvidence } from '../anonymization/engine'

export type IdentityCode = 'identity_uid_conflict' | 'identity_records_conflict' | 'identity_leading_zeros' | 'identity_records_differ'

export interface IdentityFinding {
  code: IdentityCode
  level: 'block' | 'warning'
}

/** The findings in the order a file lists them, with their levels. */
const FINDINGS: readonly IdentityFinding[] = [
  { code: 'identity_uid_conflict', level: 'block' },
  { code: 'identity_records_conflict', level: 'block' },
  { code: 'identity_leading_zeros', level: 'warning' },
  { code: 'identity_records_differ', level: 'warning' },
]

/** The codes that hold a file. */
export const IDENTITY_HOLDS: ReadonlySet<string> = new Set(FINDINGS.filter((finding) => finding.level === 'block').map((finding) => finding.code))

const UIDS = ['study', 'series', 'instance'] as const

/** A file taking part: its key, its pseudonym and its analysis's `identity`. */
export type IdentityFile = readonly [key: string, patient: string | null | undefined, evidence: IdentityEvidence | null | undefined]

type Group = { keys: string[]; patients: Set<string> }

/**
 * The blocks and warnings of the files taking part, by key; files without findings are left out.
 * The caller passes the originals the uploader has not left out, held or not. It depends only on
 * these values, so any order gives the same result.
 */
export function identityFindings(files: readonly IdentityFile[]): Record<string, IdentityFinding[]> {
  const takingPart = files.filter((file): file is readonly [string, string, IdentityEvidence] => Boolean(file[1] && file[2]))
  const found = new Map<string, Set<IdentityCode>>()
  const mark = (keys: readonly string[], code: IdentityCode) => {
    for (const key of keys) found.set(key, (found.get(key) ?? new Set<IdentityCode>()).add(code))
  }
  const grouped = (kind: keyof IdentityEvidence): Group[] => {
    const groups = new Map<string, Group>()
    for (const [key, patient, evidence] of takingPart) {
      const value = evidence[kind]
      if (!value) continue
      const group = groups.get(value) ?? { keys: [], patients: new Set<string>() }
      group.keys.push(key)
      group.patients.add(patient)
      groups.set(value, group)
    }
    return [...groups.values()]
  }

  for (const kind of UIDS) {
    // One source UID under several patient IDs.
    for (const group of grouped(kind)) if (group.patients.size > 1) mark(group.keys, 'identity_uid_conflict')
  }
  // Patient IDs that differ only in leading zeros.
  for (const group of grouped('unpadded')) if (group.patients.size > 1) mark(group.keys, 'identity_leading_zeros')
  const byPatient = new Map<string, { keys: string[]; evidence: IdentityEvidence[] }>()
  for (const [key, patient, evidence] of takingPart) {
    const files = byPatient.get(patient) ?? { keys: [], evidence: [] }
    files.keys.push(key)
    files.evidence.push(evidence)
    byPatient.set(patient, files)
  }
  for (const { keys, evidence } of byPatient.values()) {
    const names = new Set(evidence.flatMap((one) => (one.name ? [one.name] : [])))
    const dates = new Set(evidence.flatMap((one) => (one.birthDate ? [one.birthDate] : [])))
    // Among files with both, two names and two birth dates mean two files that differ in both.
    const both = evidence.filter((one) => one.name && one.birthDate)
    if (new Set(both.map((one) => one.name)).size > 1 && new Set(both.map((one) => one.birthDate)).size > 1) mark(keys, 'identity_records_conflict')
    else if (names.size > 1 || dates.size > 1) mark(keys, 'identity_records_differ')
  }
  const findings: Record<string, IdentityFinding[]> = {}
  for (const key of [...found.keys()].sort()) findings[key] = FINDINGS.filter((finding) => found.get(key)!.has(finding.code)).map((finding) => ({ ...finding }))
  return findings
}
